/**
 * O teste grátis do Viewer Pro, do lado da tela (out/2026).
 *
 * O botão «Viewer Pro» do link Starter oferece «Teste o Viewer Pro
 * gratuitamente por 7 dias». O teste é da EMPRESA do link (o `organization_id`
 * que a integração manda na emissão): o clique libera o Pro em todos os links
 * dela, pelo prazo que o gerenciador decide. Quem diz se há oferta, teste em
 * curso ou teste encerrado é o servidor (`public_view.pro_trial`); aqui mora o
 * que a tela faz com isso — os textos do botão e do cartão — e a régua da tela
 * «Testes do Pro» do gerenciador. Puro, para caber num `node --test`.
 */
import type { PublicViewProTrial } from "./api";

/** Um teste na tela «Testes do Pro». */
export interface ProTrial {
  id: string;
  organization_id: string;
  organization_name: string | null;
  requested_by_name: string;
  requested_by_email: string | null;
  started_at: string;
  ends_at: string;
  /** O prazo em dias, contado do começo. */
  days: number;
  state: "active" | "ended";
  updated_at: string | null;
  updated_by: string | null;
}

/** A oferta como está nas Configurações. */
export interface ProTrialOffer {
  enabled: boolean;
  test_organizations: string[];
  days: number;
}

export interface ProTrialList {
  offer: ProTrialOffer;
  items: ProTrial[];
  total: number;
}

/** O teto do prazo, o mesmo do servidor. */
export const PRO_TRIAL_MAX_DAYS = 365;

const DIA_MS = 86_400_000;

/** Quantos dias faltam, contando o dia em curso; 0 quando acabou. */
export function proTrialDaysLeft(endsAt: string | null | undefined, now: Date = new Date()): number {
  if (!endsAt) return 0;
  const fim = new Date(endsAt).getTime();
  if (Number.isNaN(fim)) return 0;
  const resto = fim - now.getTime();
  return resto <= 0 ? 0 : Math.ceil(resto / DIA_MS);
}

function dias(n: number): string {
  return `${n} ${n === 1 ? "dia" : "dias"}`;
}

/** O que falta, no botão e no cartão: «faltam 3 dias», «último dia». */
export function proTrialRemainingLabel(daysLeft: number): string {
  if (daysLeft <= 0) return "encerrado";
  if (daysLeft === 1) return "último dia";
  return `faltam ${dias(daysLeft)}`;
}

/** O botão da oferta: a frase do dono, com o prazo da configuração. */
export function proTrialOfferLabel(days: number): string {
  return `Teste o Viewer Pro gratuitamente por ${dias(days)}`;
}

/** A oferta no selo PRO de uma ferramenta, onde a frase inteira não cabe. */
export function proTrialSealLabel(days: number): string {
  return `Teste grátis por ${dias(days)}`;
}

/**
 * O que o cartão do convite mostra. `null` é «sem teste»: o convite de antes,
 * que leva à página do Pro.
 *
 * O `active` que já venceu no relógio da tela (a aba ficou aberta) é tratado
 * como encerrado — o servidor diria o mesmo no próximo pedido.
 */
export type ProTrialCardMode = "offer" | "active" | "ended";

export function proTrialCardMode(
  trial: PublicViewProTrial | null | undefined,
  now: Date = new Date(),
): ProTrialCardMode | null {
  if (!trial) return null;
  if (trial.state === "available") return "offer";
  if (trial.state === "active") return proTrialDaysLeft(trial.ends_at, now) > 0 ? "active" : "ended";
  return "ended";
}

/** O complemento do botão recolhido: «7 dias grátis», «faltam 3 dias» ou nada. */
export function proTrialPillSuffix(
  trial: PublicViewProTrial | null | undefined,
  now: Date = new Date(),
): string | null {
  const modo = proTrialCardMode(trial, now);
  if (modo === "offer" && trial) return `${dias(trial.days)} grátis`;
  if (modo === "active" && trial) return proTrialRemainingLabel(proTrialDaysLeft(trial.ends_at, now));
  return null;
}

/** «da Embalagens São João» quando o link traz o nome; senão «da sua empresa». */
export function proTrialCompanyOf(trial: PublicViewProTrial | null | undefined): string {
  const nome = trial?.organization_name?.trim();
  return nome ? `da ${nome}` : "da sua empresa";
}

/** O `utm_content` do clique para a página do Pro feito de dentro do teste. */
export function proTrialUtmContent(modo: ProTrialCardMode | null): string | null {
  if (modo === "active") return "teste-pro";
  if (modo === "ended") return "teste-encerrado";
  return null;
}

/**
 * A frase da recusa do clique. O status vem do `ApiError`; sem ele (rede
 * caída), a frase de tentar de novo.
 */
export function proTrialStartError(erro: unknown): string {
  const status = typeof erro === "object" && erro !== null ? (erro as { status?: unknown }).status : undefined;
  if (status === 409) return "O teste grátis do Viewer Pro desta empresa já terminou.";
  if (status === 403) return "O teste grátis do Viewer Pro não está disponível neste link.";
  return "Não foi possível liberar o teste agora. Tente de novo em instantes.";
}

/** O dia e a hora, como o resto do gerenciador: «28/10/2026 15:00». */
export function proTrialDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return iso;
  const dois = (n: number) => String(n).padStart(2, "0");
  return (
    `${dois(data.getDate())}/${dois(data.getMonth() + 1)}/${data.getFullYear()} ` +
    `${dois(data.getHours())}:${dois(data.getMinutes())}`
  );
}

/** Só o dia: «28/10/2026». */
export function proTrialDate(iso: string | null | undefined): string {
  return proTrialDateTime(iso).split(" ")[0];
}

/** O fim no cartão: «28/10/2026 às 15:00». */
export function proTrialUntil(iso: string | null | undefined): string {
  const [dia, hora] = proTrialDateTime(iso).split(" ");
  return hora ? `${dia} às ${hora}` : dia;
}

/** A situação de uma linha da tela «Testes do Pro». */
export function proTrialStatusLabel(item: Pick<ProTrial, "state" | "ends_at">, now: Date = new Date()): string {
  const restantes = proTrialDaysLeft(item.ends_at, now);
  if (item.state !== "active" || restantes <= 0) return "Encerrado";
  return `Em teste · ${proTrialRemainingLabel(restantes)}`;
}

/** O prazo digitado na tela: um inteiro de 1 a 365, ou `null`. */
export function parseProTrialDays(texto: string): number | null {
  const limpo = texto.trim();
  if (!/^\d+$/.test(limpo)) return null;
  const valor = Number(limpo);
  return valor >= 1 && valor <= PRO_TRIAL_MAX_DAYS ? valor : null;
}

/** O que muda no fim ao trocar o prazo: o começo mais os dias. */
export function proTrialEndFor(startedAt: string, days: number): string {
  return new Date(new Date(startedAt).getTime() + days * DIA_MS).toISOString();
}

/** O cabeçalho da tela: a oferta como as Configurações a deixaram. */
export function proTrialOfferSummary(offer: ProTrialOffer): string {
  if (offer.enabled) {
    return `Oferta ligada: todo link Starter com empresa oferece o teste de ${dias(offer.days)}.`;
  }
  if (offer.test_organizations.length > 0) {
    const quantas = offer.test_organizations.length;
    return (
      `Oferta desligada para os clientes — só ${quantas === 1 ? "a empresa de teste a vê" : `as ${quantas} empresas de teste a veem`}` +
      ` (${offer.test_organizations.join(", ")}), com ${dias(offer.days)}.`
    );
  }
  return "Oferta desligada: nenhum link oferece o teste.";
}
